import type { ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import Big from 'big.js';
import { toBig } from '../../../../utils/number';
import { AgGridPrice } from '../../../AgGrid/AgGridPrice';
import { Text } from '../../../Text';
import { baseColumn } from '../baseColumn';
import type { ColDefFactory, Column } from '../types';
import { numericColumnComparator } from '../utils';
import { getStrikeFwdPriceColumnValue, type StrikeFwdPriceRow } from './getStrikeFwdPriceColumnValue';
import { getStrikePriceColumnValue } from './getStrikePriceColumnValue';
import type { StrikeFwdPriceParams, StrikeFwdPriceValue } from './strikeFwdPrice.types';

function strikeFwdPriceComparator(a: StrikeFwdPriceValue | undefined, b: StrikeFwdPriceValue | undefined) {
  if (a == null && b == null) {
    return 0;
  }
  if (a == null) {
    return -1;
  }
  if (b == null) {
    return 1;
  }
  if (a.type === 'text' && b.type === 'text') {
    return (a.value ?? '').localeCompare(b.value ?? '');
  }
  if (a.type === 'text') {
    return -1;
  }
  if (b.type === 'text') {
    return 1;
  }
  return numericColumnComparator(a.value, b.value);
}

export const strikeFwdPrice: ColDefFactory<Column<StrikeFwdPriceParams>> = column => ({
  initialWidth: 150,
  minWidth: 70,
  maxWidth: 210,
  ...baseColumn(column),
  type: 'numericColumn',
  headerClass: () => {
    const columnGroupClass = column.columnGroup ? 'ag-custom-column-group' : '';
    return column.params?.align !== 'left' ? `ag-right-aligned-header ${columnGroupClass}` : `${columnGroupClass}`;
  },
  cellClass: () => {
    return column.params?.align !== 'left' ? 'ag-right-aligned-cell' : '';
  },
  valueGetter: (params: ValueGetterParams): StrikeFwdPriceValue | undefined => {
    const symbolField = column.params?.symbolField;
    if (symbolField) {
      return getStrikePriceColumnValue(params, symbolField);
    }
    return getStrikeFwdPriceColumnValue(params as ValueGetterParams<StrikeFwdPriceRow>);
  },
  cellRenderer: ({ value, data, context }: ICellRendererParams<unknown, StrikeFwdPriceValue>) => {
    if (value == null) {
      return <></>;
    }
    if (value.type === 'text') {
      if (value.value == null || value.value === '') {
        return <></>;
      }
      return <Text>{value.value}</Text>;
    }
    if (value.value == null || value.value === '') {
      return <></>;
    }
    return (
      <AgGridPrice
        value={{
          price: toBig(value.value),
          quoteCurrency: value.currency,
          asset: undefined,
          endPrice: undefined,
          aggregate: false,
          isBestPrice: false,
          isRate: false,
          shouldFormatAsUSTreasury: false,
          confirm: undefined,
        }}
        data={data}
        context={context}
        increment={value.increment}
        align={column.params?.align}
        showFeeIcon="never"
      />
    );
  },
  valueFormatter: ({ value }: ValueFormatterParams<unknown, StrikeFwdPriceValue>): string => {
    if (value == null) {
      return '';
    }
    if (value.type === 'text') {
      return value.value ?? '';
    }
    if (value.value == null || value.value === '') {
      return '';
    }
    try {
      return Big(value.value).toFixed();
    } catch {
      return value.value;
    }
  },
  comparator: strikeFwdPriceComparator,
});
